/* Bed&Bloom cinematic reveal controller — no React dependency. */
(() => {
  'use strict';
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = matchMedia('(max-width: 760px)');
  const allowed = () => !reduce.matches && !document.body.classList.contains('reduced-motion');
  const clamp = (v,a=0,b=1)=>Math.min(b,Math.max(a,v));
  const allScenes = [...document.querySelectorAll('[data-cinema]')];
  const activeScenes = new Set();
  let animationFrame = 0;

  function maskHeading(h) {
    if (h.dataset.masked) return;
    h.dataset.masked='true';
    const lines=[[]];
    for(const n of [...h.childNodes]) {
      if(n.nodeName==='BR') lines.push([]);
      else lines[lines.length-1].push(n);
    }
    h.replaceChildren();
    lines.forEach((nodes,i)=>{
      const wrapper=document.createElement('span');
      wrapper.className='motion-line';
      const inside=document.createElement('span');
      inside.className='motion-line-inner';
      inside.style.setProperty('--line',String(i));
      inside.append(...nodes);
      wrapper.append(inside);
      h.append(wrapper);
    });
  }
  const pending=new WeakSet();
  function reveal(el){ el.classList.remove('motion-pending'); el.classList.add('motion-revealed'); }
  const entranceObserver=('IntersectionObserver' in window) ? new IntersectionObserver(entries=>{
    for(const entry of entries) if(entry.isIntersecting){ reveal(entry.target); entranceObserver.unobserve(entry.target); }
  },{rootMargin:'0px 0px -4% 0px',threshold:.06}):null;

  function enhance(root=document){
    root.querySelectorAll('[data-text-reveal]').forEach(h => { maskHeading(h); if (!h.closest('[data-reveal]') && !h.closest('.hero')) h.classList.add('motion-revealed'); });
    const elements=[...root.querySelectorAll('[data-reveal]')];
    elements.forEach((el,idx)=>{
      if(pending.has(el)) return;
      pending.add(el);
      const kind=el.dataset.reveal;
      let delay=0;
      if(kind==='collection') delay=(idx%5)*100;
      else if(kind==='product') delay=(idx%4)*85;
      else if(kind==='bath-panel') delay=(idx%3)*110;
      else if(kind==='rule') delay=(idx%6)*60;
      el.style.setProperty('--reveal-delay',`${delay}ms`);
      if(!allowed()||!entranceObserver) { reveal(el);return; }
      const rect=el.getBoundingClientRect();
      if(rect.bottom<0 || rect.top>window.innerHeight*1.2){
        el.classList.add('motion-pending');
      } else if(rect.top>=window.innerHeight*.65){
        el.classList.add('motion-pending');
      }
      if(!el.classList.contains('motion-pending')) reveal(el);
      else entranceObserver.observe(el);
    });
    const hero=document.querySelector('.hero');
    if(hero&&!hero.classList.contains('is-introduced')) {
      requestAnimationFrame(()=>requestAnimationFrame(()=>hero.classList.add('is-introduced')));
    }
  }

  function paint(){
    animationFrame=0;
    if(!allowed() || document.hidden) return;
    const height=window.innerHeight;
    for(const scene of activeScenes){
      const rect=scene.getBoundingClientRect();
      const entered=clamp((height-rect.top)/(height*.9));
      const eased=1-Math.pow(1-entered,3);
      const isSmall=mobile.matches;
      const baseInset=isSmall?3:(scene.dataset.cinema==='linen'?10:9);
      scene.style.setProperty('--film-inset',`${(baseInset*(1-eased)).toFixed(2)}%`);
      scene.style.setProperty('--film-scale',(1+(isSmall?.025:.075)*(1-eased)).toFixed(4));
      scene.style.setProperty('--copy-y',`${(isSmall?0:clamp(-rect.top*.016,-12,12)).toFixed(2)}px`);
    }
  }
  function schedule(){
    if(!animationFrame&&activeScenes.size&&allowed()&&!document.hidden) animationFrame=requestAnimationFrame(paint);
  }

  if('IntersectionObserver' in window){
    const sceneObserver=new IntersectionObserver(entries=>{
      for(const e of entries){ if(e.isIntersecting) activeScenes.add(e.target);else activeScenes.delete(e.target); }
      schedule();
    },{threshold:0,rootMargin:'15% 0px'});
    allScenes.forEach(s=>sceneObserver.observe(s));
  } else { allScenes.forEach(s=>activeScenes.add(s)); }
  window.addEventListener('scroll',schedule,{passive:true});
  window.addEventListener('resize',schedule,{passive:true});

  if('IntersectionObserver' in window){
    const videos=new IntersectionObserver(entries=>{
      for(const e of entries){
        const video=e.target;
        if(e.isIntersecting){
          if(!video.getAttribute('src')&&video.dataset.src){ video.src=video.dataset.src;video.load(); }
          if(allowed()&&!video.dataset.userPaused) video.play().catch(()=>{});
        }else if(!video.paused){ video.pause(); }
      }
    },{rootMargin:'150px 0px',threshold:.02});
    document.querySelectorAll('.film-video').forEach(v=>videos.observe(v));
  }

  document.querySelectorAll('.film-toggle').forEach(btn=>{
    const v=document.getElementById(btn.dataset.video);
    if(!v)return;
    const text=btn.querySelector('.toggle-text');
    const icon=btn.querySelector('.toggle-icon');
    const sync=()=>{
      const playing=!v.paused;
      if(text)text.textContent=playing?'Pause film':'Play film';
      if(icon)icon.textContent=playing?'Ⅱ':'▷';
      btn.setAttribute('aria-pressed',String(playing));
    };
    v.addEventListener('play',sync);
    v.addEventListener('pause',sync);
    btn.addEventListener('click',()=>{
      if(v.paused){
        delete v.dataset.userPaused;
        if(!v.src&&v.dataset.src){v.src=v.dataset.src;v.load();}
        v.play().catch(()=>{});
      }else{v.dataset.userPaused='true';v.pause();}
    });
    sync();
  });

  const checkbox=document.getElementById('reduce-motion');
  if(checkbox)checkbox.addEventListener('change',()=>{
    document.body.classList.toggle('reduced-motion',checkbox.checked);
    if(checkbox.checked){ document.querySelectorAll('video').forEach(v=>v.pause());document.querySelectorAll('.motion-pending').forEach(reveal); }
    else schedule();
  });
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>enhance(),{once:true});
  else enhance();
  document.addEventListener('shopify:section:load',e=>enhance(e.target));
})();
